import { useTranslation } from "react-i18next";
import { Checkbox } from "./ui/checkbox";
import { ArrowCounterClockwise, Trash, Quotes } from "@phosphor-icons/react";

const TRASH_RETENTION_DAYS = 30;

function daysRemaining(deletedAt) {
  if (!deletedAt) return TRASH_RETENTION_DAYS;
  const elapsedDays = Math.floor((Date.now() - new Date(deletedAt).getTime()) / 86400000);
  return Math.max(TRASH_RETENTION_DAYS - elapsedDays, 0);
}

export function TrashCaptureCard({ capture, selected, onToggleSelect, onRestore, onPermanentDelete }) {
  const { t } = useTranslation();
  const isText = capture.type === "text";
  const handlePermanentDelete = () => {
    if (window.confirm(t("trashCard.confirmPermanentDelete"))) {
      onPermanentDelete(capture.id);
    }
  };

  return (
    <div
      data-testid={`trash-capture-card-${capture.id}`}
      className={`relative border bg-card ${selected ? "border-primary brutal-shadow-sm" : "border-border"}`}
    >
      <div className="absolute top-2 left-2 z-10">
        <Checkbox
          data-testid={`trash-select-checkbox-${capture.id}`}
          checked={selected}
          onCheckedChange={() => onToggleSelect(capture.id)}
          className="bg-background border-2"
        />
      </div>

      {isText ? (
        <div className="aspect-[4/3] overflow-hidden bg-secondary flex flex-col p-4 opacity-60">
          <Quotes size={22} className="text-primary shrink-0 mb-2" weight="fill" />
          <p className="text-sm leading-relaxed line-clamp-6 whitespace-pre-wrap">{capture.text_content}</p>
        </div>
      ) : (
        <div className="aspect-[4/3] overflow-hidden bg-muted flex items-center justify-center opacity-60">
          <img src={capture.image_data} alt={capture.title} className="w-full h-full object-contain" />
        </div>
      )}

      <div className="p-3 space-y-2">
        <p className="font-semibold text-sm truncate">{capture.title}</p>
        <p className="font-mono-tag text-[10px] text-muted-foreground" data-testid={`trash-days-remaining-${capture.id}`}>
          {t("trashCard.daysRemaining", { days: daysRemaining(capture.deleted_at) })}
        </p>
        <div className="flex gap-2">
          <button
            data-testid={`trash-restore-${capture.id}`}
            onClick={() => onRestore(capture.id)}
            className="flex-1 flex items-center justify-center gap-1 text-xs border border-border py-1.5 hover:bg-secondary transition-colors"
          >
            <ArrowCounterClockwise size={14} /> {t("trashCard.restore")}
          </button>
          <button
            data-testid={`trash-permanent-delete-${capture.id}`}
            onClick={handlePermanentDelete}
            className="flex-1 flex items-center justify-center gap-1 text-xs border border-border py-1.5 text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors"
          >
            <Trash size={14} /> {t("trashCard.permanentDelete")}
          </button>
        </div>
      </div>
    </div>
  );
}
